import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiDownload,
    FiEdit,
    FiEye,
    FiMessageSquare,
    FiPlus,
    FiSearch,
    FiTrash2,
    FiUpload
} from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api.config';

const getImageSrc = (image) => {
  if (!image) return '';
  if (typeof image === 'string') return image;
  return image.url || image.uri || image.secure_url || image.path || '';
};

const formatUploadedTimestamp = (dateValue) => {
  if (!dateValue) return 'N/A';

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CarManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch cars from API
  useEffect(() => {
    const fetchCars = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('agent_token');
        const agentId = localStorage.getItem('agent_id');
        
        console.log('🔍 Fetching cars from API...');
        console.log('Token exists:', !!token);
        console.log('Agent ID:', agentId);
        
        // Fetch cars - backend now handles agent logic
        // Agent will get: their own cars + cars from sellers they created
        // All statuses included (active, sold, pending, inactive, blocked)
        // Soft-deleted cars excluded
        const response = await fetch(getApiUrl('/cars'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response data:', data);
        
        if (data.success) {
          const allCars = data.data.cars || data.data || [];
          console.log('✅ Total cars loaded:', allCars.length);
          
          // No need to filter - backend returns the correct cars
          setCars(allCars);
        } else {
          console.error('❌ API returned success:false', data.message);
          setError(data.message || 'Failed to fetch cars');
        }
      } catch (err) {
        console.error('❌ Error fetching cars:', err);
        setError('Failed to fetch cars');
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  const filteredCars = cars.filter(car => {
    const matchesSearch = car.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         car.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         car.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || car.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (carId) => {
    if (!carId) {
      toast.error('Invalid car id');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this car?')) return;

    try {
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/cars/${carId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      
      if (response.ok) {
        // Remove from UI immediately
        setCars(prevCars => prevCars.filter(car => car._id !== carId));
        toast.success('Car deleted successfully');
        console.log('✅ Car deleted:', carId);
      } else {
        console.error('❌ Delete failed:', data);
        toast.error(data.message || 'Failed to delete car');
      }
    } catch (error) {
      console.error('❌ Error deleting car:', error);
      toast.error('Error deleting car');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'available': return 'bg-green-100 text-green-800';
      case 'sold': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Car Management</h1>
          <p className="mt-2 text-gray-600">Manage all your car listings and inventory</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Link to="/cars/bulk-upload">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-4 py-2 border border-orange-300 text-orange-700 bg-orange-50 font-medium rounded-xl hover:bg-orange-100 transition-all duration-200"
            >
              <FiUpload className="mr-2 h-4 w-4" />
              Bulk Upload
            </motion.button>
          </Link>
          <Link to="/cars/add">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-6 py-2 gradient-bg text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <FiPlus className="mr-2 h-4 w-4" />
              Add Car
            </motion.button>
          </Link>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiEye className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Cars</p>
              <p className="text-2xl font-semibold text-gray-900">{cars.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiEye className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-semibold text-gray-900">
                {cars.filter(car => car.status === 'active').length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiEye className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Sold</p>
              <p className="text-2xl font-semibold text-gray-900">
                {cars.filter(car => car.status === 'sold').length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 card-shadow"
        >
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FiEye className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">
                {cars.filter(car => car.status === 'pending').length}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-6 card-shadow"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search cars, sellers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="sold">Sold</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
          >
            <FiDownload className="mr-2 h-4 w-4" />
            Export
          </motion.button>
        </div>
      </motion.div>

      {/* Cars Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : error ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-50 border border-red-200 rounded-xl p-6"
        >
          <p className="text-red-600">Error: {error}</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCars.map((car, index) => {
            const carId = car._id || car.id;
            return (
            <motion.div
              key={`car-${carId}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-xl overflow-hidden card-shadow hover:shadow-xl transition-all duration-300"
            >
              <div className="relative">
                <img
                  src={
                    car.images && car.images.length > 0 
                      ? (getImageSrc(car.images[0]) || 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400')
                      : 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400'
                  }
                  alt={`${car.brand} ${car.model}`}
                  onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400'; }}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(car.status)}`}>
                    {car.status ? car.status.charAt(0).toUpperCase() + car.status.slice(1) : 'Unknown'}
                  </span>
                  {car.seller && car.seller._id && car.seller._id !== localStorage.getItem('agent_id') && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"/>
                      </svg>
                      {car.seller.firstName || car.seller.businessName || 'Seller'}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {car.brand} {car.model}
                  </h3>
                  <span className="text-lg font-bold text-orange-600">
                    ₹{(car.price / 100000).toFixed(1)}L
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-4">{car.year} • {car.fuelType} • {car.transmission}</p>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-gray-500">KM Driven</p>
                    <p className="font-semibold">{car.kmDriven?.toLocaleString() || '0'} km</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Mileage</p>
                    <p className="font-semibold">{car.mileage ? `${car.mileage} km/l` : 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-gray-500">Color</p>
                    <p className="font-semibold">{car.color || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Location</p>
                    <p className="font-semibold">{car.location?.city || 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-50 rounded-lg">
                      <FiEye className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Views</p>
                      <p className="font-semibold text-blue-600">{car.views || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-50 rounded-lg">
                      <FiMessageSquare className="h-3.5 w-3.5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Inquiries</p>
                      <p className="font-semibold text-green-600">{(car.inquiryCustomers?.length || 0) + (car.testRideCustomers?.length || 0)}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-xs text-gray-500 mb-3">
                    Uploaded: {formatUploadedTimestamp(car.createdAt)}
                  </p>
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (!carId) {
                          toast.error('Invalid car id');
                          return;
                        }
                        navigate(`/dashboard/cars/${carId}`);
                      }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                      <FiEye className="h-3.5 w-3.5" />
                      View
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (!carId) {
                          toast.error('Invalid car id');
                          return;
                        }
                        navigate(`/dashboard/cars/edit/${carId}`);
                      }}
                      className="flex-1 px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                      <FiEdit className="h-3.5 w-3.5" />
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleDelete(carId)}
                      className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                      <FiTrash2 className="h-3.5 w-3.5" />
                      Delete
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )})}
        </div>
      )}

      {filteredCars.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-gray-500 text-lg">No cars found matching your criteria.</p>
        </motion.div>
      )}
    </div>
  );
};

export default CarManagement;